<template>
  <div class="aura-quill" :data-multiline="!inline">
    <div v-once ref="contentEl" v-html="sanitizeHTML(content, 'safe-html')" />
  </div>
</template>

<script lang="ts">
import { BlotConstructor } from 'parchment'
import type { QuillOptions } from 'quill'
import Quill from 'quill'
import MagicUrl from 'quill-magic-url'

// @ts-expect-error MagicUrl is imported as module with vite 8.0.0
Quill.register('modules/magicUrl', MagicUrl?.default ?? MagicUrl)

// we don’t want Quill to add target="_blank" to link elements
// and need to perform all kind of type shenanigans to accomplish that :(
const Link = Quill.import('formats/link') as {
  create(value: string): HTMLElement
  new (...args: any[]): typeof Link
}
Quill.register(
  'formats/link',
  class CustomLink extends Link {
    static create(value: string) {
      const node = Link.create(value)
      node.removeAttribute('target')
      return node
    }
  } as unknown as BlotConstructor,
  true,
)

const Block = Quill.import('blots/block') as BlotConstructor
class InlineBlock extends Block {
  static tagName = 'span'
}

class BlockQuill extends Quill {
  constructor(container: string | HTMLElement, options?: QuillOptions) {
    Quill.register(Block, true)
    super(container, options)
  }
}

class InlineQuill extends Quill {
  constructor(container: string | HTMLElement, options?: QuillOptions) {
    Quill.register(InlineBlock, true)
    super(container, options)
  }
}
</script>

<script lang="ts" setup>
import { sanitizeHTML } from '@/util'
import { useEventListener } from '@vueuse/core'
import { onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue'

const content = defineModel<string>({ required: true })
const props = defineProps<{
  placeholder?: string
  disabled?: boolean
  inline?: boolean
  id?: string
  lang?: string
  ariaDescribedby?: string
  ariaLabelledby?: string
}>()
const emit = defineEmits<{ blur: [FocusEvent] }>()
const contentEl = ref<HTMLElement>()
const quill = ref<Quill>()
let cleanupBlurListener: ReturnType<typeof useEventListener>

function initializeQuill(contentEl: HTMLElement) {
  const config: QuillOptions = {
    placeholder: props.placeholder,
    theme: 'bubble',
    readOnly: props.disabled,
    modules: {
      magicUrl: true,
      toolbar: [['bold', 'italic', 'underline', 'strike']],
      keyboard: {
        bindings: [],
      },
    },
  }

  if (!props.inline) {
    // @ts-expect-error type definition says config.modules.toolbar might be undefined
    config.modules.toolbar.push(['link'], [{ list: 'ordered' }, { list: 'bullet' }])
  } else {
    // @ts-expect-error type definition says config.modules.keyboard might be undefined
    config.modules.keyboard.bindings.push({ key: 'Enter', shiftKey: null, handler: () => false })
  }

  const quillInstance = props.inline
    ? new InlineQuill(contentEl, config)
    : new BlockQuill(contentEl, config)

  // Disable Tab binding.
  // Users expect that they can switch to the next field.
  delete quillInstance.keyboard.bindings.Tab

  quillInstance.on('text-change', () => {
    const html = quillInstance
      .getSemanticHTML()
      // quill has a habit of injecting empty container elements
      .replaceAll('<p></p>', '')
      .replaceAll('<span></span>', '')
      .trim()
    // If the text content is empty, we’d rather assign an empty string to trigger
    // any empty/blank string handling in the parent component.
    const isEmpty = html === '' || sanitizeHTML(html, 'strip').trim() === ''
    content.value = isEmpty ? '' : html
  })

  // bubble blur events from the editor field
  cleanupBlurListener = useEventListener(
    quillInstance.editor.scroll.domNode,
    'blur',
    (event: FocusEvent) => {
      emit('blur', event)
    },
  )

  quill.value = quillInstance
}

watch(
  () => props.disabled,
  (disabled) => quill.value?.enable(!disabled),
)

onMounted(() => {
  initializeQuill(contentEl.value as HTMLElement)
})

function syncAttrs() {
  const _id = props.id
  const _ariaLabelledby = props.ariaLabelledby
  const _ariaDescribedby = props.ariaDescribedby
  const _lang = props.lang
  const _inline = props.inline
  const editor = quill.value?.container?.querySelector?.('[contenteditable="true"]')
  if (!editor) return
  editor.setAttribute('role', 'textbox')
  if (_inline) editor.setAttribute('aria-multiline', 'false')
  else editor.removeAttribute('aria-multiline')
  if (_id) editor.setAttribute('id', _id)
  else editor.removeAttribute('id')
  if (_ariaLabelledby) editor.setAttribute('aria-labelledby', _ariaLabelledby)
  else editor.removeAttribute('aria-labelledby')
  if (_ariaDescribedby) editor.setAttribute('aria-describedby', _ariaDescribedby)
  else editor.removeAttribute('aria-describedby')
  if (_lang) editor.setAttribute('lang', _lang)
  else editor.removeAttribute('lang')
}

watchEffect(syncAttrs)

onBeforeUnmount(() => {
  quill.value = undefined
  cleanupBlurListener?.()
})

useEventListener(contentEl, 'click', (event: PointerEvent) => {
  if (
    event.target &&
    'tagName' in event.target &&
    String(event.target.tagName).toLowerCase() === 'a' &&
    'getAttribute' in event.target &&
    typeof event.target.getAttribute === 'function'
  ) {
    event.preventDefault()
    event.stopPropagation()
    window.open(event.target.getAttribute('href'))
  }
})
</script>

<style lang="postcss">
.aura-quill {
  min-width: 200px;
  --_aura-quill-bg: theme('colors.gray.800');

  .ql-container {
    font-size: inherit;
    font-family: inherit;
    color: #495057;
  }
  .ql-blank::before {
    color: #9fa6b2;
    font-size: inherit;
    font-style: normal;
    font-family: inherit;
  }
  .ql-disabled .ql-editor > * {
    cursor: default;
  }
  .ql-tooltip {
    z-index: 100;
    background-color: var(--_aura-quill-bg) !important;
  }
  .ql-tooltip-arrow {
    border-bottom-color: var(--_aura-quill-bg) !important;
  }
  .ql-editor {
    @apply tw-rounded
    tw-border
    tw-border-gray-200
    tw-text-gray-600
    tw-bg-white
    tw-ring-aura-primary
    tw-transition-colors
    disabled:tw-bg-gray-100
    ro:tw-bg-gray-100
    focus:tw-outline-none
    focus-within:not-disabled:tw-ring-2
    focus:not-disabled:tw-ring-2
    dark:tw-bg-neutral-800
    dark:tw-border-neutral-700
    dark:tw-text-neutral-200
    dark:disabled:tw-bg-neutral-700
    dark:ro:tw-bg-neutral-700;

    padding: theme('spacing[1.5]') theme('spacing.3');

    &[contenteditable='false'] {
      @apply tw-bg-gray-100 dark:tw-bg-neutral-700;
    }
  }

  &[data-multiline] .ql-editor {
    min-height: calc(theme('spacing[1.5]') + 6px + 2lh);
  }
}
</style>
